
import { InjectEvent } from 'ng2-app/modules/shared/stores/events/inject.event';
import { Model } from '../models/model.class';
import { ModelEvent } from 'ng2-app/modules/shared/stores/events/model.event';
import { ModelService } from '../services/model.service';
import { Observable, ReplaySubject, Subject, of, combineLatest } from 'rxjs';
import { take, tap } from 'rxjs/operators';

export class ModelStore<M extends Model<M>> {

    private collectionLoaded = false;
    private collection: ReplaySubject<M[]> = new ReplaySubject();
    private instances: { [id: number]: ReplaySubject<M|null> } = {};
    private _events: Subject<ModelEvent<M>>;

    constructor(private service: ModelService<M>) {
        this._events = new Subject<ModelEvent<M>>();
    }

    get(id: number): Observable<M|null> {
        if (this.instances[id]) {
            return this.subjectFor(id).asObservable();
        }
        const subject = this.subjectFor(id);

        this.service.get(id).pipe(take(1)).subscribe((instance) => {
            this.inject(instance);
            this.updateCollection();
        });

        return subject.asObservable();
    }

    getAll(params = {}): Observable<(M | null)[]> {
        if (!this.collectionLoaded) {
            this.collectionLoaded = true;
            this.service.getAll(params).pipe(take(1)).subscribe(instances => {
                instances.forEach(instance => this.inject(instance));
                this.updateCollection();
            });
        }
        return this.collection.asObservable();
    }

    getCached(id: number): Observable<M | null> {
        if (this.instances[id]) {
            return this.subjectFor(id).asObservable();
        }
        return of(null);
    }

    inject(instance: M, emit= true) {
        this.subjectFor(instance.id).next(instance);
        if (emit) {
            this._events.next(new InjectEvent<M>(instance));
        }
        this.afterInject(instance);
    }

    save(model: M) {
        return this.service.save(model).pipe(
            tap((instance) => {
                this.inject(instance);
                this.updateCollection();
            })
        );
    }

    delete(model: M) {
        return this.service.delete(model).pipe(
            tap((_) => {
                this.subjectFor(model.id).next(null);
                this.subjectFor(model.id).complete();
                this.updateCollection();
            })
        );
    }

    get events(): Observable<ModelEvent<M>> {
        return this._events.asObservable();
    }

    protected afterInject(_model: M) {
        // May be used by concrete implementations
    }

    private subjectFor(id: number): ReplaySubject<M|null> {
        if (!this.instances[id]) {
            this.instances[id] = new ReplaySubject<M>(1);
        }
        return this.instances[id];
    }

    private updateCollection() {
        combineLatest(Object.values(this.instances)).subscribe(instances => {
            this.collection.next(instances.filter(x => !!x) as M[]);
        });
    }
}
